import { Typography, Stack, Divider, Button } from "@mui/material";
import { fromUnixTime } from "date-fns";
import { Fragment } from "react";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";

import { RowChoice, RowBase } from "./Rows/RowBase";
import { ErrorsCard } from "../../../components/Layout/ErrorsCard";
import { use404Page } from "../../../hooks/use404Page";
import {
    useResponseCombiner,
    ServiceResponse,
} from "../../../hooks/useServiceResponse";
import { useWorkOrderViewService } from "../../../service/WorkOrderService";

interface WorkOrderViewContentProps {
    workOrderId: number;
}

function WorkOrderViewContent({ workOrderId }: WorkOrderViewContentProps) {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const { workOrderResponse } = useWorkOrderViewService(workOrderId);
    const response = useResponseCombiner({ workOrder: workOrderResponse });

    return (
        <ServiceResponse
            response={response}
            options={{ errorComponent: ErrorsCard }}
        >
            <Helmet
                title={`${t("pages.workorder.header_view")} - ${
                    workOrderResponse.data?.customerName
                } - ${response.data.workOrder?.customerLocationName} - ${
                    response.data.workOrder?.workListName
                }`}
            />
            <Stack gap={3}>
                <Button
                    variant="contained"
                    onClick={() =>
                        navigate(`/app/workorders/${workOrderId}/print`)
                    }
                    sx={{
                        width: "min-content",
                        whiteSpace: "nowrap",
                    }}
                >
                    Document printen
                </Button>
                <Divider flexItem />
                <Typography variant="h5">Vragenlijst</Typography>
                <Stack gap={3}>
                    {workOrderResponse.data?.answers.map((answer, idx) => (
                        <Fragment key={`workorder_row_${answer.id}`}>
                            {answer.question !== undefined && idx > 0 && (
                                <Divider flexItem />
                            )}
                            <RowChoice
                                answer={answer}
                                controlPoints={
                                    workOrderResponse.data?.controlPoints ?? []
                                }
                                meta={{
                                    imageColumns: 4,
                                }}
                            />
                        </Fragment>
                    ))}
                </Stack>
                <Divider flexItem />
                <Typography variant="h5" sx={{ marginTop: 3, marginBlock: 3 }}>
                    Afronding
                </Typography>
                <Stack gap={3}>
                    <RowBase question="Werkzaamheden verricht door">
                        <Typography>
                            {workOrderResponse.data?.performedBy}
                        </Typography>
                    </RowBase>
                    <RowBase question="Bezoekdatum">
                        <Typography>
                            {workOrderResponse.data
                                ? fromUnixTime(
                                      workOrderResponse.data.visitationDate,
                                  ).toLocaleDateString(undefined, {
                                      dateStyle: "full",
                                  })
                                : "-"}
                        </Typography>
                    </RowBase>
                    <RowBase question="Handtekening">
                        <img
                            src={`data:image/png;base64, ${workOrderResponse.data?.signature}`}
                            alt="Werkbon handtekening"
                            style={{
                                border: "1px solid black",
                                width: "100%",
                            }}
                        />
                    </RowBase>
                    <RowBase question="Ondertekend door">
                        <Typography>
                            {workOrderResponse.data?.signedBy}
                        </Typography>
                    </RowBase>
                    <RowBase question="Opmerkingen">
                        <Typography>
                            {workOrderResponse.data?.comment ?? "-"}
                        </Typography>
                    </RowBase>
                </Stack>
            </Stack>
        </ServiceResponse>
    );
}

function WorkOrderViewErrorRedirect() {
    use404Page(true);
    return <></>;
}

export function WorkOrderViewPage() {
    const { workOrderId } = useParams();

    let workOrderIdParsed: number | undefined = undefined;
    if (workOrderId) {
        workOrderIdParsed = parseInt(workOrderId, 10);
        if (isNaN(workOrderIdParsed)) {
            workOrderIdParsed = undefined;
        }
    }

    if (workOrderIdParsed === undefined) {
        return <WorkOrderViewErrorRedirect />;
    } else {
        return <WorkOrderViewContent workOrderId={workOrderIdParsed} />;
    }
}
